<template>
  <div class="page-gutter">
    <!-- The page is a 30-day read, so it doesn't claim "Today"; the Allen intro shows on
         the first few visits only (it costs a row on every return visit). -->
    <Section :title="$t('demo.home.title', { store: 'Newell DIY' })">
      <!-- Design scenario (?scenario=…): the demo reshaped into another store type. -->
      <div v-if="scenario" class="scenario-chip">
        <i class="bi bi-diagram-3"></i> {{ $t('demo.home.scenario', { label: scenario.label }) }} ·
        <router-link to="/storalytic_demo_store">{{ $t('demo.home.backToMixed') }}</router-link>
      </div>
      <div v-if="showIntro" class="home-intro">
        <p class="intro-lead">
          <i18n-t keypath="demo.home.introLead" scope="global"><template #allen><span class="intro-name">Allen</span><span class="intro-bot" aria-hidden="true"><i class="bi bi-robot"></i></span></template></i18n-t>
        </p>
        <p class="intro-sub">{{ $t('demo.home.introSub') }}</p>
      </div>

      <!-- The SAME production TodayView component, driven by static demo data so this
           open demo shows exactly how the live "/" landing looks. Keyed by scenario so a
           switch remounts it (it copies the flagged items once, at mount). -->
      <TodayView v-if="todayDemoData" :key="scenario ? scenario.label : 'mixed'" :demo-data="todayDemoData" />

      <!-- Ask Allen moved to the global docked companion (AllenDock in App.vue). -->
    </Section>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { SCENARIOS } from '@/demo/homeScenarios.js'
import Section from '@/components/ui/Section.vue'
import TodayView from './TodayView.vue'
import { withNl } from '@/demo/demoLang'
import { currentLanguage } from '@/i18n'

// Allen intro: first INTRO_VISITS visits on this browser, then gone. Storage can be
// blocked (private window, previews) → fall back to showing it.
const INTRO_VISITS = 3
const showIntro = (() => {
  try {
    const n = Number(localStorage.getItem('stora_demo_home_visits') || 0) + 1
    localStorage.setItem('stora_demo_home_visits', String(n))
    return n <= INTRO_VISITS
  } catch (_) { return true }
})()
import { loadDemoJSON } from '@/api/demoData.js'
import { demoValueDelivered } from '@/demo/guidanceDemo.js'
import { DEMO_ALLEN_NOTIFICATIONS } from '@/demo/allenNotificationsDemo'
import { useNotificationCenterStore } from '@/stores/NotificationCenterStore'

// Seed the notification center so the nav bell (From Allen tab) and Today's
// "Allen also flagged" preview share one source (idempotent).
useNotificationCenterStore().seedDemo()

const demoCoachDigest = ref(null)

// --- Today surface (production TodayView, demo-fed) --------------------------
// Mirrors the demo dashboard's data assembly so the "/" briefing reads a zone
// against the same ceilings: base fixed-widgets JSON + the demo peer cohort, the
// DIY sector, and the per-zone own-best overrides (same values as the demo tab).
const fixedWidgetsData = ref(null)
const compareData = ref(null)
const demoZones = ref([])
const DEMO_SECTOR = 'diy'
// Own-best rungs per zone, tuned for a diverse demo: Power Tools is at its
// engagement ceiling (hold); Garden has engagement headroom; Safety Shoes'
// own-best REACH (0.15) makes reach its lever; Promoted's conversion is the gap.
const DEMO_ZONE_OVERRIDES = {
  'Power Tools':            { pastPeakEngagementP95: 0.50, rung1Engagement: 0.50 },
  'Garden Utilities':       { pastPeakEngagementP95: 0.46, rung1Engagement: 0.46 },
  'Safety Shoes':           { pastPeakEngagementP95: 0.44, rung1Engagement: 0.44, rung1Reach: 0.15 },
  'Promoted Articles Zone': { pastPeakEngagementP95: 0.42, rung1Engagement: 0.42 },
  // Tripwire zone: judged on its OWN best weeks only, with its normal weekly swing.
  'Paint & Decor':          { rung1Reach: 0.19, reachWobble: 0.015 },
}
// Paint & Decor is a TRIPWIRE zone: it counts who arrives, nothing else. Same numbers
// as its reach card on the demo Actions tab (1,716 of 11,320 shoppers ≈ 15.2% reach,
// 19% in its best weeks). Added to Today's copy only — the Explore/Zones demo pages
// read the shared widgets file and show the four polygon zones.
const withTripwireZone = (fw) => ({
  ...fw,
  product_zone_visitors_counts: { ...fw.product_zone_visitors_counts, 'Paint & Decor': 1716 },
  product_zone_clear_lingerers_counts: { ...fw.product_zone_clear_lingerers_counts, 'Paint & Decor': 0 },
  product_zone_funnels: { ...fw.product_zone_funnels, 'Paint & Decor': { store_visitors: 11320, zone_visitors: 1716 } },
  product_zones_geometry: { ...(fw.product_zones_geometry || {}), 'Paint & Decor': 'tripwire' },
})
// Demo SERVE four-rung (mirrors the backend _service.serve contract + the demo tab):
// current wait 6:00, own-best 4:00, peers 3:20, best-in-class 3:00. In production this
// rides on the live counters route; here it's injected into the demo fixed-widgets.
const DEMO_SERVICE = {
  serve: {
    headlinePct: 66.7, peerTickPct: 120, ceilingTickPct: 133.3,
    ownBestWaitSec: 240, cohortWaitSec: 200, currentWaitSec: 360, targetWaitSec: 180,
    activeRung: 'your best weeks', activeRungKey: 'own',
    waitReductionMin: 2.0,
    atRiskEur: 9400, atRiskRange: [6100, 12700], atRiskEst: true, atFloor: false,
    // where the backend really carries the store ladder (signals compute_service_gauge)
    climb: { activeRung: 'your best weeks', activeRungKey: 'own', waitReductionMin: 2.0,
             currentWaitSec: 360, atFloor: false },
  },
  waiting: [
    { zoneType: 10, zoneTypeName: 'Service', headlinePct: 73, peerTickPct: 118, ceilingTickPct: null,
      activeRung: 'your best weeks', waitReductionMin: 0.8, withinReachEur: null, atFloor: false },
    { zoneType: 9, zoneTypeName: 'Info', headlinePct: 88, peerTickPct: 106, ceilingTickPct: null,
      activeRung: 'your best weeks', waitReductionMin: 0.3, withinReachEur: null, atFloor: false },
    { zoneType: 11, zoneTypeName: 'Pick Up', headlinePct: 100, peerTickPct: 112, ceilingTickPct: null,
      activeRung: null, waitReductionMin: null, withinReachEur: null, atFloor: true },
  ],
}
const DEMO_RECOMMENDATIONS = {
  'Power Tools': withNl({
    action: 'Set up a plug-in demo unit customers can pick up and trigger, with a clear spec-and-price card and a "call an expert" button beside it.',
    why: 'Dwell here is healthy — the drop-off is the last step: turning genuine interest into a decision on an expensive tool.',
  }, {
    action: 'Zet een demotoestel klaar dat bezoekers kunnen oppakken en aanzetten, met een duidelijke kaart met specificaties en prijs en een knop "roep een expert" ernaast.',
    why: 'De verblijftijd is hier gezond. Het loopt mis bij de laatste stap: echte interesse omzetten in een beslissing over een duur toestel.',
  }),
  'Safety Shoes': withNl({
    action: 'Bring a low display of the full size run to the aisle edge with a clear "sizes 39–48 in stock" sign, so the category registers from the main walkway.',
    why: 'Only about 7% of shoppers reach this aisle — versus roughly 15% at its best. It sits off the main route, so few people find it; the ones who do engage and buy well.',
  }, {
    action: 'Zet een lage display met alle maten aan de rand van het gangpad, met een duidelijk bord "maten 39–48 op voorraad", zodat de categorie al vanaf het hoofdpad opvalt.',
    why: 'Maar ongeveer 7% van je bezoekers komt in dit gangpad, tegen zo\'n 15% in je beste weken. Het ligt naast de hoofdroute, dus weinig mensen vinden het; wie er komt, blijft staan en koopt goed.',
  }),
  'Garden Utilities': withNl({
    action: 'Build a small focal display at the mouth of the aisle — one hero product staged for use — so passing shoppers have a reason to stop.',
    why: 'About 42% of your shoppers pass Garden but only ~22% stop. Traffic isn\'t the problem — the range just doesn\'t catch the eye from the aisle.',
  }, {
    action: 'Bouw een kleine blikvanger aan het begin van het gangpad (één topproduct, opgesteld alsof het in gebruik is), zodat voorbijlopende bezoekers een reden hebben om te blijven staan.',
    why: 'Ongeveer 42% van je bezoekers komt langs Garden, maar maar ~22% blijft staan. Het aantal bezoekers is niet het probleem: het assortiment valt vanuit het gangpad gewoon niet op.',
  }),
  'Paint & Decor': withNl({
    action: 'Turn the aisle end that faces the main walkway into a colour-of-the-month wall — swatches, one finished sample board — so Paint & Decor registers from the route most shoppers already take.',
    why: 'About 15% of your shoppers reach Paint & Decor; in its best weeks it was 19%.',
  }, {
    action: 'Maak van de kop van het gangpad die naar het hoofdpad kijkt een wand met de kleur van de maand (kleurstalen en één afgewerkt proefpaneel), zodat Paint & Decor opvalt vanaf de route die de meeste bezoekers al nemen.',
    why: 'Ongeveer 15% van je bezoekers komt bij Paint & Decor; in je beste weken was dat 19%.',
  }),
  'Promoted Articles Zone': withNl({
    action: 'Add a bold price-and-benefit card and a limited-time cue to the promo, so the shoppers who stop get a clear reason to buy now.',
    why: 'Plenty of shoppers stop at the promo, but only about 14% buy — the offer isn\'t closing the sale.',
  }, {
    action: 'Zet bij de promo een opvallende kaart met prijs en voordeel en een duidelijke einddatum, zodat wie blijft staan een reden heeft om nu te kopen.',
    why: 'Veel bezoekers blijven staan bij de promo, maar maar ongeveer 14% koopt: het aanbod haalt de verkoop niet binnen.',
  }),
}
// ?scenario=polygon → the same bundle reshaped into a polygon-only store (design aid;
// see demo/homeScenarios.js).
const route = useRoute()
const scenario = computed(() => SCENARIOS[route.query.scenario] || null)
const todayDemoData = computed(() => {
  const d = baseDemoData.value
  if (!d) return null
  return scenario.value ? scenario.value.apply(d) : d
})
const baseDemoData = computed(() => {
  if (!fixedWidgetsData.value) return null
  return {
    companyName: 'Newell DIY',
    fixedWidgetsData: fixedWidgetsData.value,
    compareData: compareData.value || {},
    zones: demoZones.value,
    sector: DEMO_SECTOR,
    zoneOverrides: DEMO_ZONE_OVERRIDES,
    recommendations: DEMO_RECOMMENDATIONS,
    staffCost: null,
    // Attribution of DEPLOY's idle-staffing euro. Concentrated enough to be worth a
    // sentence — the same guards the real staffing grid applies (a real queue, a clear
    // lift, a run of consecutive hours) before it is allowed to claim a pattern.
    idleWhen: currentLanguage() === 'nl'
      ? 'Het meeste valt op dinsdag- en woensdagochtend, voor 11:00.'
      : 'Most of it falls on Tuesday and Wednesday mornings, before 11:00.',
    findings: findings.value,
    digest: demoCoachDigest.value,
    goals,
    valueDelivered,
  }
})


const goals = [
  // Aligned with the computed zone levers: Garden = engagement gap (biggest €),
  // Safety Shoes = reach gap, Promoted Articles = conversion gap.
  withNl({ title: 'Grow engagement in Garden Utilities', lever: 'engagement' },
    { title: 'Meer engagement in Garden Utilities' }),
  withNl({ title: 'Draw more shoppers to Safety Shoes', lever: 'reach' },
    { title: 'Meer bezoekers naar Safety Shoes' }),
  withNl({ title: 'Lift conversion on Promoted Articles Zone', lever: 'conversion' },
    { title: 'Hogere conversie in Promoted Articles Zone' }),
]
// PROVE — CALCULATED from the same tracked demo actions + € math the demo Actions tab
// uses (demo/guidanceDemo.js), never retyped: run-rate = its "/ month" roll-up,
// cumulative = its "Delivered by Allen". Rounded to €10 exactly as that tab shows them.
const round10 = (n) => Math.round(n / 10) * 10
const valueDelivered = (() => {
  const v = demoValueDelivered()
  if (!v) return null
  const since = new Date(v.since + 'T00:00:00')
  return withNl({
    runRate: round10(v.runRate),
    cumulative: round10(v.cumulative),
    since: since.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
  }, { since: since.toLocaleDateString('nl', { month: 'long', year: 'numeric' }) })
})()

// What Allen noticed — the "what's changing" layer. Each finding states its OWN
// timeframe (`when`) because, unlike the 30-day cards/digest, these come from
// different windows: a forecast (recent-weeks trend + next-7-day projection) and
// month-over-month checkout waits. Copy mirrors what coach/findings.py produces.
// Single source with the notification center (the nav bell → From Allen tab): Today's
// "Allen also flagged" is a read-only PREVIEW of the same items the center manages.
const findings = ref(DEMO_ALLEN_NOTIFICATIONS)

onMounted(async () => {
  try {
    demoCoachDigest.value = await loadDemoJSON('/demo_data/demo_coach_digest.json')
  } catch (_) { /* noop */ }
  try {
    const fw = await loadDemoJSON('/demo_data/demo_fixed_widgets.json')
    // Same demo peer cohort + SERVE four-rung the dashboard tab injects, so the
    // hero (100% of best weeks · peers next) and the SERVE gauge both match.
    fixedWidgetsData.value = { ...withTripwireZone(fw), _cohort: { engagement: 0.42 }, _service: DEMO_SERVICE }
  } catch (_) { /* noop */ }
  try {
    const cmp = await loadDemoJSON('/demo_data/demo_fixed_widgets_compare_to.json')
    // Previous-period SERVE headline so the gauge's delta renders (62 → +5pp vs 67).
    compareData.value = { ...cmp, _service: { serve: { headlinePct: 61.7 } } }
  } catch (_) { /* compare is optional */ }
  try {
    const { detectionzones } = await loadDemoJSON('/demo_data/demo_detection_zones.json')
    demoZones.value = detectionzones || []
  } catch (_) { /* zone counts fall back to 0 */ }
})
</script>

<style scoped>
.scenario-chip {
  display: inline-flex; align-items: center; gap: 6px; margin: 0 0 14px;
  padding: 4px 12px; border-radius: 999px; font-size: 12.5px; font-weight: 600;
  background: #fff4e0; border: 1px solid #f1d9a8; color: #8a5a00;
}
.scenario-chip a { color: #8a5a00; text-decoration: underline; }
/* Intro — the one place Allen is introduced: a two-tier lead/sub, with his
   avatar inline right after his name. */
.home-intro { display: flex; flex-direction: column; gap: 0.14rem; margin: 0 0 1.4rem; }
.intro-lead { margin: 0; font-size: 1.18rem; font-weight: 700; color: #1d2b3a; line-height: 1.3; }
.intro-name { color: var(--stora-accent, #4F7EB3); }
.intro-bot {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px; height: 24px;
  margin-left: 0.32rem;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--stora-accent, #4F7EB3), var(--stora-accent-2, #62B5D9));
  color: #fff;
  font-size: 0.8rem;
  vertical-align: middle;
  position: relative;
  top: -0.18em;
  box-shadow: 0 2px 6px rgba(79, 126, 179, 0.3);
}
.intro-sub { margin: 0; font-size: 0.92rem; color: var(--stora-muted, #6b7a86); line-height: 1.5; max-width: 640px; }

/* Goals card, now standalone under the Today surface (transitional — Stage 4 folds
   it into TodayView's Value-delivered / Your-goals row). */
.goals-standalone { margin-top: 30px; max-width: 1060px; }

/* ===== ABOVE the divider: period-independent signals, grouped on a light tint ===== */
.home-top {
  display: grid;
  grid-template-columns: minmax(0, 1.7fr) minmax(260px, 1fr);
  gap: var(--gap-xl, 24px);
  align-items: start;
  background: #f7faff;
  border: 1px solid #eef2f8;
  border-radius: 14px;
  padding: 1.2rem 1.25rem 1.3rem;
}
/* Flatten the nested cards so two white boxes on the tint don't read as heavy. */
.home-top .panel,
.home-top .noticed-bubble { box-shadow: 0 2px 6px rgba(13, 38, 76, 0.04); }
@media (max-width: 1080px) {
  .home-top { grid-template-columns: 1fr; }
}

/* Allen noticed — a speech balloon, sibling to the digest (avatar + tailed bubble) */
.noticed-card { display: flex; align-items: flex-start; gap: 0.9rem; }
.noticed-avatar {
  position: relative;
  flex: 0 0 auto;
  width: 46px; height: 46px;
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  background: linear-gradient(135deg, var(--stora-accent, #4F7EB3), var(--stora-accent-2, #62B5D9));
  color: #fff;
  font-size: 1.4rem;
  box-shadow: 0 4px 10px rgba(79, 126, 179, 0.35);
}
.noticed-avatar .bell-badge {
  position: absolute;
  right: -2px; bottom: -2px;
  width: 20px; height: 20px;
  border-radius: 50%;
  background: #fff;
  border: 2px solid #fff;
  box-shadow: 0 0 0 1px var(--stora-border, #e6edf3);
  display: flex; align-items: center; justify-content: center;
  font-size: 11px;
  color: var(--stora-accent, #4F7EB3);
}
.noticed-bubble {
  position: relative;
  flex: 1 1 auto;
  background: var(--stora-card, #fff);
  border: 1px solid var(--stora-border, #e6edf3);
  border-radius: 12px;
  padding: 1rem 1.25rem 1.1rem;
  box-shadow: 0 6px 14px rgba(13, 38, 76, 0.06);
}
.noticed-bubble::before {
  content: '';
  position: absolute;
  top: 18px; left: -8px;
  width: 14px; height: 14px;
  background: var(--stora-card, #fff);
  border-left: 1px solid var(--stora-border, #e6edf3);
  border-bottom: 1px solid var(--stora-border, #e6edf3);
  transform: rotate(45deg);
}
.noticed-head { display: flex; align-items: baseline; gap: 0.5rem; margin-bottom: 0.7rem; }
.noticed-name { font-weight: 700; color: var(--stora-accent, #4F7EB3); }
.noticed-role { font-size: 0.8rem; color: var(--stora-muted, #6b7a86); }

.home-findings { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 0.5rem; }
.finding {
  display: flex;
  align-items: flex-start;
  gap: 0.4rem;
  border-left: 3px solid #8fa2b7;
  background: #f7f9fc;
  border-radius: 0 8px 8px 0;
  padding: 0.5rem 0.55rem 0.5rem 0.6rem;
}
.finding-body { display: flex; flex-direction: column; gap: 0.1rem; min-width: 0; }
.finding-title { font-weight: 600; font-size: 0.84rem; color: #1d2b3a; line-height: 1.3; }
.finding-detail { font-size: 0.78rem; color: #51637a; line-height: 1.4; }
.finding-dismiss {
  margin-left: auto;
  background: none; border: none;
  color: #9aa7b5; cursor: pointer;
  font-size: 0.72rem; padding: 0.1rem 0.15rem; line-height: 1;
}
.finding-dismiss:hover { color: #51637a; }
.finding.sev-info    { border-left-color: #62B5D9; background: #f4f8fc; }
.finding.sev-warning { border-left-color: #D3A741; background: #fff8ec; }
.finding.sev-high    { border-left-color: #C21500; background: #fff3f1; }
.finding.sev-high .finding-title { color: #9a2727; }

/* Per-finding timeframe — each signal is on its own clock. */
.finding-when {
  align-self: flex-start;
  margin-top: 0.3rem;
  font-size: 0.68rem;
  color: var(--stora-muted, #6b7a86);
  background: #eef2f6;
  border-radius: 999px;
  padding: 0.1rem 0.5rem;
  display: inline-flex; align-items: center; gap: 0.28rem;
}
.finding-when i { font-size: 0.62rem; }

/* Goals card */
.panel {
  background: var(--stora-card, #fff);
  border: 1px solid var(--stora-border, #e6edf3);
  border-radius: 12px;
  box-shadow: 0 6px 14px rgba(13, 38, 76, 0.06);
}
.home-panel { padding: 1rem 1.15rem; }
.home-panel-head {
  display: flex; align-items: center; gap: 0.45rem;
  font-weight: 600; color: #344054; margin-bottom: 0.7rem;
}
.home-panel-head i { color: var(--stora-accent, #4F7EB3); }
.home-goals { list-style: none; padding: 0; margin: 0; }
.home-goals li { display: flex; flex-direction: column; gap: 0.25rem; color: #344054; }
.home-goals li + li { border-top: 1px solid var(--stora-border, #e6edf3); padding-top: 0.55rem; margin-top: 0.55rem; }
.goal-title { font-weight: 500; font-size: 0.9rem; }
.goal-tag {
  align-self: flex-start;
  font-size: 0.72rem;
  color: var(--stora-accent, #4F7EB3);
  background: #eef5ff;
  border: 1px solid #dbe7f5;
  border-radius: 999px;
  padding: 0.12rem 0.6rem;
}
/* Manage-goals affordance — active link in the real app; greyed in the demo. */
.goals-manage {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  margin-top: 0.85rem;
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--stora-accent, #4F7EB3);
  text-decoration: none;
}
.goals-manage i { font-size: 0.75rem; }
.goals-manage.is-disabled { color: #b4bfca; cursor: default; }

/* ===== The divider: the period pill scopes everything below it ===== */
.period-divider {
  display: flex;
  align-items: center;
  gap: 0.7rem;
  margin: 1.7rem 0 1.3rem;
}
.period-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  flex: none;
  font-size: 0.82rem;
  color: var(--stora-muted, #6b7a86);
  background: #f4f7fb;
  border: 1px solid var(--stora-border, #e6edf3);
  border-radius: 999px;
  padding: 0.25rem 0.7rem;
}
.period-pill i { color: var(--stora-accent, #4F7EB3); }
.period-label {
  flex: none;
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #344054;
}
.period-divider::after {
  content: '';
  flex: 1 1 auto;
  height: 1px;
  background: var(--stora-border, #e6edf3);
}

/* ===== BELOW the divider: the 30-day snapshot ===== */
.home-alert {
  display: flex;
  align-items: flex-start;
  gap: 0.55rem;
  background: #fff3f1;
  border: 1px solid #f3b3a8;
  border-left: 4px solid #C21500;
  color: #9a2727;
  border-radius: 10px;
  padding: 0.7rem 0.95rem;
  margin: 0 0 1.1rem;
  font-size: 0.9rem;
  line-height: 1.45;
}
.home-alert i { color: #C21500; font-size: 1rem; margin-top: 0.1rem; }
.home-alert strong { font-weight: 700; }

.home-kpis {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--gap-md, 14px);
  margin: 0 0 0.6rem;
  transition: opacity 0.2s ease;
}
.home-kpis.kpis-caveated { opacity: 0.62; }
@media (max-width: 1080px) {
  .home-kpis { grid-template-columns: 1fr; }
}
.kpi-card {
  display: block;
  text-decoration: none;
  background: var(--stora-card, #fff);
  border: 1px solid var(--stora-border, #e6edf3);
  border-left: 4px solid var(--accent, #4F7EB3);
  border-radius: 12px;
  padding: 0.95rem 1.1rem;
  box-shadow: 0 3px 12px rgba(13, 38, 76, 0.05);
  transition: box-shadow 0.15s ease, transform 0.15s ease;
}
.kpi-card:hover { box-shadow: 0 8px 18px rgba(13, 38, 76, 0.1); transform: translateY(-2px); }
.kpi-label { font-size: 0.85rem; color: var(--stora-muted, #6b7a86); margin-bottom: 0.15rem; }
.kpi-val   { font-size: 2rem; font-weight: 800; line-height: 1.15; }
.kpi-foot  { font-size: 0.8rem; color: #9aa7b1; margin-top: 0.1rem; }

.home-kpis-foot { display: flex; justify-content: flex-end; margin: 0 0 1.6rem; }
.home-breakdown-link {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--stora-accent, #4F7EB3);
  text-decoration: none;
}
.home-breakdown-link:hover { text-decoration: underline; }
.home-breakdown-link i { font-size: 0.78rem; transition: transform 0.15s ease; }
.home-breakdown-link:hover i { transform: translateX(3px); }

/* Ask Allen — its own always-available tool, not part of the period snapshot,
   so it's separated from the digest by a hairline. */
.home-ask {
  margin-top: 1.6rem;
  padding-top: 1.6rem;
  border-top: 1px solid var(--stora-border, #e6edf3);
}
</style>
